import { Vector3 } from 'three';
import {
  MUZZLE_SPEED,
  PITCH_MAX,
  PITCH_MIN,
  aimDirection,
  launchPitch,
  marchToImpact,
} from '../utils/ballistics';
import { FIRING_LINE_Z, terrainHeight, terrainSlope } from './heightfield';

/**
 * Plan du champ de tir : ou se trouvent les dix cibles.
 *
 * Il vit ici, et pas dans `entities/Targets.ts`, parce que deux modules en
 * dependent : les cibles elles-memes, et le decor, qui doit laisser le champ
 * libre devant chacune. Une seule liste, donc pas de cible plantee derriere
 * un rocher.
 *
 * Les positions sont posees a la main. Un champ de tir n'est pas un terrain
 * aleatoire : on veut une progression de portee lisible, des cibles derriere
 * les merlons pour forcer le tir courbe, et de quoi varier l'azimut.
 * cf. doc/04-balistique.md
 */

export type TargetKind = 'panel' | 'moving' | 'hulk';

export interface TargetSite {
  kind: TargetKind;
  /** Position au sol, altitude resolue depuis le champ de hauteur. */
  position: Vector3;
  /** Orientation : la cible fait face a la ligne de tir. */
  yaw: number;
  /** Distance a la ligne de tir, en metres. Affichee dans le recapitulatif. */
  range: number;
  /** Cibles mobiles : demi-amplitude du va-et-vient, en metres. */
  travel: number;
  /** Cibles mobiles : periode de l'aller-retour, en secondes. */
  period: number;
}

/** Position du char au depart : toutes les portees sont mesurees depuis la. */
const ORIGIN = new Vector3(0, 0, FIRING_LINE_Z);

/**
 * Hauteur de la bouche au-dessus du sol, en metres.
 *
 * C'est une constante et non une mesure du modele : `world/` ne doit pas
 * dependre de `entities/`. 2,4 m correspond au tourillon du char a plat ; la
 * marge prise plus bas absorbe largement l'ecart.
 */
const FIRING_HEIGHT = 2.4;

/** Tolerance d'arrivee : l'obus doit tomber a moins de ca de la cible. */
const REACH_TOLERANCE = 2.5;

interface Draft {
  kind: TargetKind;
  x: number;
  z: number;
  travel?: number;
  period?: number;
}

/**
 * Les dix emplacements voulus, du plus proche au plus lointain.
 *
 * Ce sont des *souhaits* : `settle` les deplace de quelques metres si le sol
 * y est trop incline ou si l'obus n'y arrive pas. Les positions finales sont
 * dans TARGET_SITES.
 *
 * La portee maximale du canon est de 280 m sur terrain plat (v^2/g), et la
 * hausse est bornee a 35,5 degres, ce qui ramene la portee utile depuis la
 * ligne de tir a environ 265 m. La cible la plus eloignee est a 250 m : elle
 * est atteignable sans bouger, mais au prix d'une hausse de 31 degres.
 */
const DRAFTS: Draft[] = [
  { kind: 'panel', x: -18, z: -84 },
  { kind: 'panel', x: 30, z: -62 },
  { kind: 'moving', x: -52, z: -32, travel: 24, period: 11 },
  // Juste derriere le merlon de z=6 : il faut passer au-dessus.
  { kind: 'hulk', x: 16, z: 16 },
  { kind: 'panel', x: 70, z: 26 },
  { kind: 'hulk', x: -72, z: 46 },
  { kind: 'moving', x: 30, z: 68, travel: 28, period: 9 },
  { kind: 'panel', x: -26, z: 84 },
  { kind: 'hulk', x: 56, z: 92 },
  { kind: 'moving', x: -20, z: 100, travel: 22, period: 13 },
];

/**
 * L'obus peut-il reellement atteindre ce point depuis la ligne de tir ?
 *
 * On resout la hausse avec `launchPitch`, on verifie qu'elle tient dans les
 * debattements du canon, et surtout on fait voler l'obus avec
 * `marchToImpact` : s'il accroche une crete ou un merlon en chemin, l'impact
 * tombe court et la cible est refusee.
 *
 * Ce test n'est pas un luxe. Un premier jeu de positions, choisies a l'oeil et
 * validees sur la seule pente du sol, plaçait une cible a 95 m dans une cuvette
 * derriere un bourrelet : son centre etait *plus bas* que le terrain dix
 * metres devant elle. Elle etait proprement intouchable, et rien dans le code
 * ne le disait -- seul un tir le revelait.
 *
 * Valider le plan avec la balistique du jeu ferme ce trou par construction :
 * si cette fonction accepte les dix cibles, les dix sont atteignables.
 */
function reachable(x: number, z: number, centerY: number): boolean {
  const muzzle = new Vector3(
    ORIGIN.x,
    terrainHeight(ORIGIN.x, ORIGIN.z) + FIRING_HEIGHT,
    ORIGIN.z,
  );
  const dx = x - muzzle.x;
  const dz = z - muzzle.z;
  const range = Math.hypot(dx, dz);
  const pitch = launchPitch(range, centerY - muzzle.y);
  // Hors de portee, ou hors des debattements du canon.
  if (pitch === null || pitch < PITCH_MIN || pitch > PITCH_MAX) return false;

  const velocity = aimDirection(Math.atan2(dx, dz), pitch).multiplyScalar(MUZZLE_SPEED);
  const impact = marchToImpact(muzzle, velocity);
  if (!impact.hitGround) return false;
  return Math.hypot(impact.point.x - x, impact.point.z - z) < REACH_TOLERANCE;
}

/**
 * Cherche une assise acceptable autour de la position voulue.
 *
 * Deux criteres : une pente assez faible pour qu'un panneau sur pieds ne
 * penche pas comme un arbre mort, et une trajectoire qui arrive vraiment
 * jusque-la. On accepte un decalage de quelques metres pour les satisfaire ;
 * le mouvement reste petit devant les portees, et l'ordre des cibles est
 * preserve.
 */
function settle(
  x: number,
  z: number,
  maxSlope: number,
  centerHeight: number,
): { x: number; z: number } {
  const ok = (cx: number, cz: number): boolean =>
    terrainSlope(cx, cz) <= maxSlope && reachable(cx, cz, terrainHeight(cx, cz) + centerHeight);

  if (ok(x, z)) return { x, z };

  // Spirale de Vogel : elle couvre le disque regulierement, sans privilegier
  // une direction comme le ferait un balayage en croix.
  let fallback: { x: number; z: number } | null = null;
  for (let i = 1; i <= 90; i++) {
    const a = i * 2.39996;
    const rad = Math.sqrt(i) * 2.4;
    const cx = x + Math.cos(a) * rad;
    const cz = z + Math.sin(a) * rad;
    if (ok(cx, cz)) return { x: cx, z: cz };
    // A defaut, on retient le premier point simplement atteignable : mieux
    // vaut une cible un peu penchee qu'une cible intouchable.
    if (!fallback && reachable(cx, cz, terrainHeight(cx, cz) + centerHeight)) {
      fallback = { x: cx, z: cz };
    }
  }
  return fallback ?? { x, z };
}

function build(): TargetSite[] {
  return DRAFTS.map((d) => {
    const hulk = d.kind === 'hulk';
    // Une epave encaisse une pente plus forte qu'un panneau sur pieds, et son
    // centre touchable est plus bas.
    const { x, z } = settle(d.x, d.z, hulk ? 0.2 : 0.1, hulk ? 1.3 : 1.75);
    const position = new Vector3(x, terrainHeight(x, z), z);
    return {
      kind: d.kind,
      position,
      // atan2(dx, dz) et non (dz, dx) : yaw = 0 regarde vers +z.
      yaw: Math.atan2(ORIGIN.x - x, ORIGIN.z - z),
      range: Math.hypot(x - ORIGIN.x, z - ORIGIN.z),
      travel: d.travel ?? 0,
      period: d.period ?? 10,
    };
  });
}

export const TARGET_SITES: TargetSite[] = build();

export const TARGET_TOTAL = TARGET_SITES.length;

/**
 * Rayon a laisser libre autour d'une cible. Il couvre le va-et-vient des
 * cibles mobiles, sinon un rocher finit par cacher la moitie de leur course.
 */
export function clearanceRadius(site: TargetSite): number {
  return 11 + site.travel;
}

/** Vrai si (x, z) tombe dans le degagement d'une cible. */
export function inTargetClearance(x: number, z: number): boolean {
  return TARGET_SITES.some((s) => {
    const r = clearanceRadius(s);
    return (s.position.x - x) ** 2 + (s.position.z - z) ** 2 < r * r;
  });
}
